import { uniq } from 'lodash/fp';

import {
    CONSTANT_VACCINATION_ID_KEY,
    EXPANDED_WOLBACHIA_ID_KEY,
} from '@portal/common/models/data-key';
import { DataGranularityKey } from '@portal/common/types';

import config from '../config';

export type ScenarioConfig = {
    [CONSTANT_VACCINATION_ID_KEY]: number;
    [EXPANDED_WOLBACHIA_ID_KEY]: number;
    name: string;
};

export const FORECAST_SCENARIO_SPLIT_MAP: Record<number, ScenarioConfig> = {
    [101]: {
        [CONSTANT_VACCINATION_ID_KEY]: 2,
        [EXPANDED_WOLBACHIA_ID_KEY]: 1,
        name: 'Constant vaccination, mean effectiveness',
    },
    [102]: {
        [CONSTANT_VACCINATION_ID_KEY]: 3,
        [EXPANDED_WOLBACHIA_ID_KEY]: 1,
        name: 'Constant vaccination, low effectiveness',
    },
    [103]: {
        [CONSTANT_VACCINATION_ID_KEY]: 4,
        [EXPANDED_WOLBACHIA_ID_KEY]: 1,
        name: 'Constant vaccination, high effectiveness',
    },
    [104]: {
        [CONSTANT_VACCINATION_ID_KEY]: 2,
        [EXPANDED_WOLBACHIA_ID_KEY]: 2,
        name: 'Constant vaccination, mean effectiveness + Expanded Wolbachia, mean effectiveness',
    },
    [105]: {
        [CONSTANT_VACCINATION_ID_KEY]: 3,
        [EXPANDED_WOLBACHIA_ID_KEY]: 2,
        name: 'Constant vaccination, low effectiveness + Expanded Wolbachia, mean effectiveness',
    },
    [106]: {
        [CONSTANT_VACCINATION_ID_KEY]: 4,
        [EXPANDED_WOLBACHIA_ID_KEY]: 2,
        name: 'Constant vaccination, high effectiveness + Expanded Wolbachia, mean effectiveness',
    },
    [107]: {
        [CONSTANT_VACCINATION_ID_KEY]: 2,
        [EXPANDED_WOLBACHIA_ID_KEY]: 3,
        name: 'Constant vaccination, mean effectiveness + Expanded Wolbachia, low effectiveness',
    },
    [108]: {
        [CONSTANT_VACCINATION_ID_KEY]: 3,
        [EXPANDED_WOLBACHIA_ID_KEY]: 3,
        name: 'Constant vaccination, low effectiveness + Expanded Wolbachia, low effectiveness',
    },
    [109]: {
        [CONSTANT_VACCINATION_ID_KEY]: 4,
        [EXPANDED_WOLBACHIA_ID_KEY]: 3,
        name: 'Constant vaccination, high effectiveness + Expanded Wolbachia, low effectiveness',
    },
    [110]: {
        [CONSTANT_VACCINATION_ID_KEY]: 2,
        [EXPANDED_WOLBACHIA_ID_KEY]: 4,
        name: 'Constant vaccination, mean effectiveness + Expanded Wolbachia, high effectiveness',
    },
    [111]: {
        [CONSTANT_VACCINATION_ID_KEY]: 3,
        [EXPANDED_WOLBACHIA_ID_KEY]: 4,
        name: 'Constant vaccination, low effectiveness + Expanded Wolbachia, high effectiveness',
    },
    [112]: {
        [CONSTANT_VACCINATION_ID_KEY]: 4,
        [EXPANDED_WOLBACHIA_ID_KEY]: 4,
        name: 'Constant vaccination, high effectiveness + Expanded Wolbachia, high effectiveness',
    },
};

export const findScenarioKey = (vaccinationId: number, wolbachiaId: number): string | undefined => {
    if (!vaccinationId || !wolbachiaId) {
        return undefined;
    }

    return Object.keys(FORECAST_SCENARIO_SPLIT_MAP).find((key) => {
        const scenario = FORECAST_SCENARIO_SPLIT_MAP[Number(key)];
        return (
            scenario[CONSTANT_VACCINATION_ID_KEY] === vaccinationId &&
            scenario[EXPANDED_WOLBACHIA_ID_KEY] === wolbachiaId
        );
    });
};

export const getSubfilterOptions = (
    key: DataGranularityKey,
    forecastOptions: number[]
): number[] => {
    const options: number[] = uniq(
        forecastOptions
            .filter((id: number) => id !== config.dengueBaselineForecastScenarioId)
            .map((id: number) => FORECAST_SCENARIO_SPLIT_MAP[id]?.[key])
            .filter((value): value is number => value !== undefined)
    ).sort();

    const sortPriority = [1, 3, 2, 4]; // none/low/mean/high

    sortPriority.reverse().forEach((value: number) => {
        const idx = options.indexOf(value);
        if (idx > 0) {
            options.splice(idx, 1);
            options.unshift(value);
        }
    });

    return options;
};
